import {RecommendationDiscussionHostComponent} from "./modules/discussions/components/recommendation-discussion-host/recommendation-discussion-host.component";
import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
  NgZone,
  Signal,
} from "@angular/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {App} from "@capacitor/app";
import {CapacitorHttp, PluginListenerHandle} from "@capacitor/core";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {AuthService} from "./modules/auth/services/auth.service";
import {UpdatedUser, User, UserWork} from "./modules/auth/models/User";
import {Subscription, filter, take} from "rxjs";
import {Tokens} from "./modules/auth/models/User";
import {ScreenSizeService} from "./modules/shared/services/screenSize.service";
import {environment} from "src/environments/environment";
import {
  VolumesBackwardProgress,
  WorksModel,
  RecommendationInfo,
} from "./models/WorksModel";
import {
  hasStreaming,
  isBook,
  isComic,
  isGameSeries,
  isManga,
} from "src/app/utils/works-type-guards.util";
import {WorkDetailsComponent} from "src/app/modules/works/registries/works-details.registry";
import {SocketService} from "./modules/shared/services/socket.service";
import {ModalService} from "./modules/core/services/modal.service";
import {
  NotificationsModal,
  WorkDataModal,
  WorkModal,
} from "./modules/shared/interfaces/Modal.interface";
import {Toast} from "./modules/shared/interfaces/Toast.interface";
import {ToastService} from "./modules/core/services/toast.service";
import {trigger, style, transition, animate} from "@angular/animations";
import {SocketUpdate} from "./modules/shared/interfaces/Socket.interface";
import {WorksService} from "./modules/shared/services/works.service";
import {ClickService} from "./modules/shared/services/click.service";
import {BookService} from "./modules/shared/services/book.service";
import {SeriesService} from "./modules/shared/services/series.service";
import {GameService} from "./modules/shared/services/game.service";
import {MangaService} from "./modules/shared/services/manga.service";
import {ComicService} from "./modules/shared/services/comic.service";
import {TranslationService} from "./services/translation.service";
import {TranslationUpdaterService} from "./services/translationUpdater.service";
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import {ScreenSize} from "./modules/shared/interfaces/ScreenSize.interface";
import {WorksTypes} from "./enums/Works.enum";
import {isEmptyObject} from "./helpers/types";
import {GameSeries} from "./models/Game";
import {Notification} from "./interfaces/notification.interface";
import {
  CommonModule,
  NgClass,
  NgTemplateOutlet,
  NgComponentOutlet,
} from "@angular/common";
import {HeaderMobileComponent} from "./components/header-mobile/header-mobile.component";
import {HeaderComponent} from "./components/header/header.component";
import {NavbarMobileComponent} from "./components/navbar-mobile/navbar-mobile.component";
import {NavbarComponent} from "./components/navbar/navbar.component";
import {NotificationsListComponent} from "./components/notifications-list/notifications-list.component";
import {ProfileComponent} from "./components/profile/profile.component";
import {AddToListComponent} from "./modules/core/components/add-to-list/add-to-list.component";
import {AddWorkComponent} from "./modules/core/components/add-work/add-work.component";
import {CreateListComponent} from "./modules/core/components/create-list/create-list.component";
import {EditBookDatesComponent} from "./modules/core/components/edit-book-dates/edit-book-dates.component";
import {EditGameDatesComponent} from "./modules/core/components/edit-game-dates/edit-game-dates.component";
import {EditSeriesDatesComponent} from "./modules/core/components/edit-series-dates/edit-series-dates.component";
import {EditVolumesDatesComponent} from "./modules/core/components/edit-volumes-dates/edit-volumes-dates.component";
import {EditWorkDateComponent} from "./modules/core/components/edit-work-date/edit-work-date.component";
import {ModalComponent} from "./modules/core/components/modal/modal.component";
import {ToastComponent} from "./modules/core/components/toast/toast.component";
import {WorkGameSeriesComponent} from "./modules/lists/components/work-game-series/work-game-series.component";
import {DisplayStreamingOptionsComponent} from "./modules/works/components/display-streaming-options/display-streaming-options.component";
import {GuardTypePipe} from "./modules/core/pipes/guardType.pipe";
import {SendWorkComponent} from "./modules/core/components/send-work/send-work.component";
import {PublicWorkContextComponent} from "./modules/works/components/public-work-context/public-work-context.component";
import {PublicWorkEntryService} from "./modules/works/services/public-work-entry.service";
import {PushNotificationsService} from "./services/pushNotifications.service";
import {NotificationDeepLinkService} from "./services/notificationDeepLink.service";
import {Volume} from "./models/Volume";
import {AppUpdateService} from "./services/appUpdate.service";
import {NewsModalComponent} from "./modules/core/components/news-modal/news-modal.component";
import {RealtimeInvalidationService} from "./modules/shared/services/realtime-invalidation.service";
import {AppVersionInfo} from "./interfaces/appVersion.interface";
import {WorkQuickRatingComponent} from "./modules/shared/components/work-quick-rating/work-quick-rating.component";
import {WorkQuickCompletionComponent} from "./modules/shared/components/work-quick-completion/work-quick-completion.component";
import {AddWorkRatingPromptService} from "./modules/core/services/add-work-rating-prompt.service";
import {AddWorkCompletionPromptService} from "./modules/core/services/add-work-completion-prompt.service";
import type {
  RatingPromptContext,
  WorkRatingSubmit,
} from "./modules/shared/interfaces/add-work-rating-prompt.interface";
import {PwaInstallService} from "./modules/auth/services/pwa-install.service";
import {WorksCompanionComponent} from "./modules/works-companions/components/works-companion/works-companion.component";
import {CompassResultsLoadingOverlayComponent} from "./components/compass-results-loading-overlay/compass-results-loading-overlay.component";
import {FounderPremiumModalComponent} from "./modules/subscription/components/founder-premium-modal/founder-premium-modal.component";
import {UserService} from "./modules/shared/services/user.service";
import {WorkDetailsNavigationComponent} from "./modules/works/components/work-details-navigation/work-details-navigation.component";
import {WorkModalNavigationService} from "./modules/core/services/work-modal-navigation.service";
import {PlatformService} from "./services/platform.service";
import {ForceUpdateComponent} from "./components/force-update/force-update.component";
import {ActivationDeepLinkService} from "./modules/auth/services/activation-deep-link.service";
import {mergeWorkAddition} from "./utils/work-item-merge.util";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [
        style({opacity: 0}),
        animate("0.05s", style({opacity: 0})),
        animate("0.1s", style({opacity: 1})),
      ]),
      transition(":leave", [animate("0.1s", style({opacity: 0}))]),
    ]),
  ],
  imports: [
    RecommendationDiscussionHostComponent,
    CommonModule,
    RouterOutlet,
    NgClass,
    NgTemplateOutlet,
    NgComponentOutlet,
    TranslateModule,
    HeaderMobileComponent,
    NavbarComponent,
    NavbarMobileComponent,
    HeaderComponent,
    ProfileComponent,
    NotificationsListComponent,
    ModalComponent,
    CreateListComponent,
    AddToListComponent,
    ToastComponent,
    AddWorkComponent,
    DisplayStreamingOptionsComponent,
    EditGameDatesComponent,
    EditBookDatesComponent,
    EditSeriesDatesComponent,
    EditVolumesDatesComponent,
    EditWorkDateComponent,
    SendWorkComponent,
    PublicWorkContextComponent,
    NewsModalComponent,
    WorkGameSeriesComponent,
    GuardTypePipe,
    WorkQuickRatingComponent,
    WorkQuickCompletionComponent,
    WorksCompanionComponent,
    CompassResultsLoadingOverlayComponent,
    FounderPremiumModalComponent,
    WorkDetailsNavigationComponent,
    ForceUpdateComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public logged = false;
  public user?: User;
  public otherUser?: boolean;
  public friend?: User;
  public item?: WorksModel;
  public modalItems: WorksModel[] = [];

  get sendWorkDesktopClasses(): string {
    const width =
      this.modalItems.length > 1 ? "!max-w-[600px]" : "!max-w-[840px]";
    return `!h-auto !min-h-[260px] !rounded-lg ${width}`;
  }
  public notifications?: Notification[];
  public addWorkRatingPromptWork?: WorksModel;
  public addWorkRatingPromptContext?: RatingPromptContext;
  public addWorkRatingSubmit?: WorkRatingSubmit;
  public addWorkCompletionPromptWork?: WorksModel;
  private pendingRatingPromptAfterQuickCompletion?: WorksModel;
  public currentRecommendation?: RecommendationInfo;
  public currentRecommendationModalId?: string;
  public saga?: GameSeries;
  public volume?: Volume;
  public itemId?: string;
  public list?: string;
  public fullyLoaded = false;
  public detailsPreloaded = false;
  public detailsComponent!: WorkDetailsComponent;
  public isMobile = false;
  private readonly subscriptions: Subscription[] = [];
  private iosConnectivityListener?: Promise<PluginListenerHandle>;
  public online: boolean = navigator.onLine;
  public isNotificationsModalOpened = false;
  public readonly toasts: Signal<Toast[]>;
  public readonly maxVisibleToastLayers = 3;
  private readonly imagesDomain: string = environment.imagesDomain;
  public imagesPath?: string;
  public isBook = isBook;
  public isManga = isManga;
  public isComic = isComic;
  public hasStreaming = hasStreaming;
  public isGameSeries = isGameSeries;
  public isEmptyObject = isEmptyObject;
  public currentModalId?: string;
  public deleteConfirmLabel = "YES_PLEASE";
  public isClearingRating = false;
  private currentRouteClass?: string;
  public isCompassRecommendationsTunnelRoute = false;
  public isOnboardingRoute = false;
  public isAdminRoute = false;
  public isLegalCompletionRoute = false;
  public isAccountActivationRoute = false;
  public isPublicWorkRoute = false;
  private isEmailChangeRoute = false;
  private routeNavigationPending = false;
  public get isRouteLayoutTransitionPending(): boolean {
    return this.routeNavigationPending || this.publicWorkEntry.loading();
  }
  public worksTypes = WorksTypes;
  private lastKnownAppVersion?: string;
  private isAppVersionReloading = false;
  private readonly appUpdatedVersionSessionKey = "app_updated_version";
  private readonly appReloadedVersionSessionKey = "app_reloaded_version";
  private readonly compassTunnelSteps = new Set([
    "splash",
    "mode",
    "context",
    "filters",
    "results",
  ]);
  public readonly founderPremiumModalId = "founder_premium_welcome";
  private readonly founderPremiumModalOpenedForUsers = new Set<string>();
  private founderPremiumModalUpdateInProgress = false;
  private readonly socketCleanups: Array<() => void> = [];
  private readonly userUpdatedCallback = (update?: SocketUpdate) => {
    this.updateUser(update as UpdatedUser);
  };
  private readonly userDeletedCallback = () => {
    this.authService.logout();
  };

  public getWorkDetailsStickyTitle(item: WorksModel): string {
    const title = this.worksService.getItemTitle(item);

    if (this.isBook(item) && item.subtitle) {
      return `${title}: ${item.subtitle}`;
    }

    return title;
  }

  constructor(
    _translation: TranslationService,
    _translationUpdater: TranslationUpdaterService,
    public readonly appUpdateService: AppUpdateService,
    private readonly translate: TranslateService,
    private readonly authService: AuthService,
    private readonly screenSizeService: ScreenSizeService,
    private readonly socketService: SocketService,
    private readonly realtimeInvalidationService: RealtimeInvalidationService,
    public modalService: ModalService,
    private readonly toastService: ToastService,
    public worksService: WorksService,
    private readonly addWorkRatingPromptService: AddWorkRatingPromptService,
    private readonly addWorkCompletionPromptService: AddWorkCompletionPromptService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly clickService: ClickService,
    public bookService: BookService,
    public seriesService: SeriesService,
    public gameService: GameService,
    public mangaService: MangaService,
    public comicService: ComicService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly notificationDeepLinkService: NotificationDeepLinkService,
    private readonly activationDeepLinkService: ActivationDeepLinkService,
    private readonly publicWorkEntry: PublicWorkEntryService,
    public readonly workModalNavigationService: WorkModalNavigationService,
    private readonly platformService: PlatformService,
    pwaInstallService: PwaInstallService
  ) {
    void pwaInstallService;
    this.toasts = toSignal(this.toastService.toasts$, {requireSync: true});
    this.platformService.initializeNativeBackButton();
    this.initializeIosConnectivity();
    globalThis.addEventListener("online", this.handleOnlineEvent);
    globalThis.addEventListener("offline", this.handleOfflineEvent);

    const appUpdateSubscription = this.appUpdateService.appVersion$.subscribe(
      (response) => this.handleAppVersionUpdate(response)
    );

    const userSubscription = this.authService.user$.subscribe({
      next: (user: User | null) => {
        if (user) {
          this.logged = true;
          this.user = user;
          this.syncPageTheme();

          if (user.tokens?.accessToken) {
            this.socketService.emit("login", {
              userToken: user.tokens.accessToken,
            });
          }
          this.openFounderPremiumModalIfNeeded(user);
          const pushSyncSubscription = this.authService.tokens$
            .pipe(
              filter(
                (tokens): tokens is Tokens => !!tokens?.accessToken?.length
              ),
              take(1)
            )
            .subscribe(() => {
              this.pushNotificationsService
                .heartbeatOnStartup(user._id)
                .subscribe();
            });
          this.subscriptions.push(pushSyncSubscription);
          this.cdr.markForCheck();
        } else {
          this.logged = false;
        }
      },
    });

    const modalNotificationsSubscription =
      this.modalService.modalNotifications$.subscribe(
        (data?: NotificationsModal) => {
          if (data) {
            this.isNotificationsModalOpened = true;
            this.notifications = data.notifications;
          } else {
            this.notifications = undefined;
          }
          this.ngZone.run(() => {
            this.cdr.markForCheck();
          });
        }
      );

    const addWorkRatingPromptSubscription =
      this.addWorkRatingPromptService.prompt$.subscribe((prompt) => {
        this.addWorkRatingPromptWork = prompt?.work;
        this.addWorkRatingPromptContext = prompt?.context;
        this.addWorkRatingSubmit = prompt?.submitRating;
        this.ngZone.run(() => {
          this.cdr.markForCheck();
        });
      });

    const addWorkCompletionPromptSubscription =
      this.addWorkCompletionPromptService.prompt$.subscribe((prompt) => {
        this.addWorkCompletionPromptWork = prompt?.work;
        this.ngZone.run(() => {
          this.cdr.markForCheck();
        });
      });

    const addWorkActionSubscription = this.worksService.workActions$.subscribe(
      (action) => {
        if (
          action.kind !== "add_work" ||
          !action.work ||
          action.userId !== this.user?._id
        ) {
          return;
        }

        const addedWork =
          this.item?._id === action.work._id
            ? mergeWorkAddition(this.item, action.work)
            : action.work;
        if (this.shouldOpenRatingPrompt(addedWork)) {
          this.addWorkRatingPromptService.openPrompt(addedWork, {
            user: this.user,
          });
          return;
        }

        this.addWorkCompletionPromptService.openPrompt(
          this.otherUser ? action.work : addedWork
        );
      }
    );

    const modalWorkSubscription = this.modalService.modal$.subscribe(
      (data?: WorkDataModal) => {
        if (data) {
          this.item = data.item;
          this.list = data.list;
          this.otherUser = data.otherUser;
          this.friend = data.friend;
          this.detailsComponent = data.detailsComponent;
          this.imagesPath = this.imagesDomain + "/" + data.item.type;
          this.fullyLoaded = data.fullyLoaded;
          this.detailsPreloaded = data.detailsPreloaded ?? false;
        } else {
          this.item = undefined;
          this.list = undefined;
          this.otherUser = false;
          this.friend = undefined;
          this.detailsComponent = null;
          this.imagesPath = undefined;
          this.fullyLoaded = false;
          this.detailsPreloaded = false;
        }
        this.ngZone.run(() => {
          this.cdr.markForCheck();
        });
      }
    );

    const modalItemSubscription = this.modalService.modalItem$.subscribe(
      (data?: WorkModal) => {
        if (data?.item) {
          if (data.modalId?.includes("game_series")) {
            const item = data.item as GameSeries;
            this.saga = item;
            if (data.modalId) {
              this.currentModalId = data.modalId;
            }
          } else if (data.modalId?.startsWith("comment_")) {
            const item = data.item as WorksModel;
            const username = data.modalId.replace("comment_", "");
            const recommendation = item.recommendations?.find(
              (entry) => entry.friend.username === username
            );
            this.currentRecommendation = recommendation;
            if (data.modalId) {
              this.currentModalId = data.modalId;
            }
          } else {
            const item = data.item as WorksModel;
            this.item = item;
            this.modalItems = data.items ?? [item];
            this.list = data.list;
            this.imagesPath = this.imagesDomain + "/" + item.type;
            if (data.modalId) {
              this.currentModalId = data.modalId;
            }
            if (data.modalId?.startsWith("dlt_")) {
              this.deleteConfirmLabel = "YES_PLEASE";
            }

            if (data.volume) {
              this.volume = data.volume;
            }
          }
          this.ngZone.run(() => {
            this.cdr.markForCheck();
          });
        }
      }
    );

    const modalOpenedSubscription = this.modalService.modalOpened.subscribe(
      (event) => {
        if (event === null) {
          this.pendingRatingPromptAfterQuickCompletion = undefined;
          this.addWorkRatingPromptService.clearPrompt();
          this.addWorkCompletionPromptService.clearPrompt();
        }
        if (
          event?.modalId === this.addWorkRatingPromptService.modalId &&
          !event.opened
        ) {
          this.addWorkRatingPromptService.clearPrompt();
        }
        if (
          event?.modalId === this.addWorkCompletionPromptService.modalId &&
          !event.opened
        ) {
          this.addWorkCompletionPromptService.clearPrompt();
          this.openPendingRatingPromptAfterQuickCompletion();
        }
        if (event?.modalId?.startsWith("dlt_") && !event.opened) {
          this.deleteConfirmLabel = "YES_PLEASE";
          this.cdr.markForCheck();
        }
      }
    );

    const escapeSubscription = this.clickService
      .getEscapeObservable()
      .subscribe((event) => {
        if (!this.modalService.hasOpenModal()) {
          return;
        }
        event.preventDefault();
        this.modalService.closeModal();
      });

    this.socketCleanups.push(
      this.realtimeInvalidationService.subscribe(
        "userUpdated",
        this.userUpdatedCallback
      ),
      this.realtimeInvalidationService.subscribe(
        "userDeleted",
        this.userDeletedCallback
      )
    );

    this.subscriptions.push(
      appUpdateSubscription,
      userSubscription,
      modalNotificationsSubscription,
      addWorkRatingPromptSubscription,
      addWorkCompletionPromptSubscription,
      addWorkActionSubscription,
      modalWorkSubscription,
      modalItemSubscription,
      modalOpenedSubscription,
      escapeSubscription
    );
  }

  ngOnInit() {
    this.activationDeepLinkService.init();
    this.notificationDeepLinkService.init();

    if (this.user) {
      this.pushNotificationsService.listenNotificationPermission(this.user);
    }

    const initialUrl = this.getCurrentBrowserUrl();
    this.updateRouteLayoutState(initialUrl);
    this.routeNavigationPending =
      !this.router.navigated &&
      this.shouldShowRouteTransitionSplash(initialUrl);

    const routeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.handleNavigationStart(event);
      } else if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event);
      } else if (event instanceof NavigationCancel) {
        this.handleNavigationCancel(event);
      } else if (
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        this.clearRouteLayoutTransition();
      }

      this.cdr.markForCheck();
    });

    const screenSizeSubscription = this.screenSizeService.screenSize$.subscribe(
      (size: ScreenSize) => {
        this.isMobile = size.isMobile;
        this.cdr.markForCheck();
      }
    );
    this.subscriptions.push(routeSubscription, screenSizeSubscription);
  }

  updateUser(user: UpdatedUser) {
    this.authService.user = user.user;
    this.user = user.user;
    this.openFounderPremiumModalIfNeeded(user.user);
    this.cdr.markForCheck();
  }

  private openFounderPremiumModalIfNeeded(user: User): void {
    if (
      this.shouldSuppressNewsAndFounderModals() ||
      !user.subscription?.isFounder ||
      user.hasSeenFounderModal ||
      this.founderPremiumModalOpenedForUsers.has(user._id)
    ) {
      return;
    }

    this.founderPremiumModalOpenedForUsers.add(user._id);
    setTimeout(() => {
      if (this.shouldSuppressNewsAndFounderModals()) {
        this.founderPremiumModalOpenedForUsers.delete(user._id);
        return;
      }

      this.modalService.openModal(this.founderPremiumModalId);
    }, 0);
  }

  public dismissFounderPremiumModal(): void {
    const user = this.user;
    if (!user || this.founderPremiumModalUpdateInProgress) {
      return;
    }

    this.founderPremiumModalUpdateInProgress = true;
    const updateSubscription = this.userService
      .updateUser(user._id, {hasSeenFounderModal: true})
      .subscribe({
        next: (updatedUser) => {
          this.authService.user = updatedUser;
          this.user = updatedUser;
          this.modalService.closeModal(this.founderPremiumModalId);
          this.founderPremiumModalUpdateInProgress = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.founderPremiumModalUpdateInProgress = false;
          this.modalService.closeModal(this.founderPremiumModalId);
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(updateSubscription);
  }

  get mainClasses(): {[key: string]: boolean} {
    const classes: {[key: string]: boolean} = {
      isAside:
        this.logged &&
        !this.isMobile &&
        !this.isAdminRoute &&
        !this.isLegalCompletionRoute &&
        !this.isAccountActivationRoute &&
        !this.isPublicWorkRoute &&
        !this.isCompassRecommendationsTunnelRoute &&
        !this.isOnboardingRoute,
      isMobile: this.isMobile,
      unlogged:
        !this.logged ||
        this.isLegalCompletionRoute ||
        this.isAccountActivationRoute,
      compass: this.isCompassRecommendationsTunnelRoute,
      onboarding: this.isOnboardingRoute,
      admin: this.isAdminRoute,
      "!p-0": this.isPublicWorkRoute,
    };

    if (this.currentRouteClass) {
      classes[this.currentRouteClass] = true;
    }

    return classes;
  }

  onNotificationsModalClosed(): void {
    this.isNotificationsModalOpened = false;
  }

  onAddWorkRatingPromptClosed(): void {
    this.addWorkRatingPromptService.clearPrompt();
  }

  onAddWorkCompletionPromptClosed(): void {
    this.addWorkCompletionPromptService.clearPrompt();
  }

  onAddWorkQuickCompleted(updatedWork: WorksModel): void {
    this.addWorkCompletionPromptWork = updatedWork;
    this.pendingRatingPromptAfterQuickCompletion = this.shouldOpenRatingPrompt(
      updatedWork
    )
      ? updatedWork
      : undefined;
    this.syncPromptUpdatedWork(updatedWork);
    this.modalService.closeModal(this.addWorkCompletionPromptService.modalId);
  }

  onAddWorkRatingPromptRemove(): void {
    this.removeAddedPromptWork(
      this.addWorkRatingPromptWork,
      this.addWorkRatingPromptService.modalId,
      () => this.addWorkRatingPromptService.clearPrompt()
    );
  }

  onAddWorkCompletionPromptRemove(): void {
    this.removeAddedPromptWork(
      this.addWorkCompletionPromptWork,
      this.addWorkCompletionPromptService.modalId,
      () => this.addWorkCompletionPromptService.clearPrompt()
    );
  }

  onAddWorkQuickRated(rating: number): void {
    if (this.addWorkRatingSubmit) {
      this.modalService.closeModal(this.addWorkRatingPromptService.modalId);
      return;
    }
    const ratedWork = this.addWorkRatingPromptWork;
    this.addWorkRatingPromptService.markCurrentPromptRated(rating);

    let updatedWork: WorksModel | undefined;

    if (ratedWork) {
      updatedWork = {...ratedWork, userRating: rating};
      this.addWorkRatingPromptWork = updatedWork;
    }

    if (ratedWork && this.item?._id?.toString() === ratedWork._id?.toString()) {
      this.item = {...this.item, userRating: rating};
      updatedWork = this.item;
      this.modalService.updateItem(this.item, this.list);
      this.cdr.markForCheck();
    }

    if (updatedWork) {
      this.worksService.setEditedItem(updatedWork);
    }
  }

  private shouldOpenRatingPrompt(work: WorksModel): boolean {
    return this.addWorkRatingPromptService.shouldPromptForWork(work, this.user);
  }

  private openPendingRatingPromptAfterQuickCompletion(): void {
    const work = this.pendingRatingPromptAfterQuickCompletion;
    this.pendingRatingPromptAfterQuickCompletion = undefined;

    if (!work || !this.user) {
      return;
    }

    this.addWorkRatingPromptService.openPrompt(work, {user: this.user});
  }

  private syncPromptUpdatedWork(updatedWork: WorksModel): void {
    if (this.item?._id?.toString() === updatedWork._id?.toString()) {
      this.item = updatedWork;
      this.modalService.updateItem(this.item, this.list);
      this.cdr.markForCheck();
    }

    this.worksService.setEditedItem(updatedWork);
  }

  private removeAddedPromptWork(
    work: WorksModel | undefined,
    promptModalId: string,
    clearPrompt: () => void
  ): void {
    if (!work || !this.user) {
      return;
    }

    const removeWorkSubscription = this.worksService
      .removeWorkById(this.user._id, work._id, work.type)
      .subscribe({
        next: () => {
          clearPrompt();
          this.modalService.closeModal(promptModalId);
          if (this.item?._id?.toString() === work._id?.toString()) {
            this.modalService.closeModal(work._id);
          }
          this.worksService.setEditedItem({...work, inUserGemster: false});
          this.toastService.sendToast({
            text: "WORK_DELETED",
            classes: "bg-form-success",
          });
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastService.sendToast({
            text: "ERROR_UPDATE",
            classes: "bg-form-error",
          });
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(removeWorkSubscription);
  }

  closeModal(modalId: string) {
    if (modalId.startsWith("dlt_")) {
      this.deleteConfirmLabel = "YES_PLEASE";
    }
    this.modalService.closeModal(modalId);
  }

  clearRating(item: WorksModel): void {
    if (this.isClearingRating || !this.user?._id || !item?._id) {
      return;
    }

    const userId = this.user._id;
    const workId = item._id;
    const workType = item.type;
    const clearRatingModalId = "clear_rating_" + workId;
    this.isClearingRating = true;
    this.cdr.markForCheck();
    const clearRatingSubscription = this.worksService
      .updateWorkRatingByType(userId, workId, workType, null)
      .subscribe({
        next: () => {
          this.closeModal(clearRatingModalId);
          const isCurrentWork =
            this.item?._id?.toString() === workId.toString();
          const clearedItem = {
            ...(isCurrentWork && this.item ? this.item : item),
          };
          Object.assign(clearedItem, {userRating: undefined});

          this.addWorkRatingPromptService.markLocalRatingCleared(clearedItem);
          this.clearRatingFromCurrentUser(clearedItem);
          this.worksService.setEditedItem(clearedItem);

          if (isCurrentWork) {
            this.item = clearedItem;
            this.modalService.updateItem(clearedItem, this.list);
          }

          this.isClearingRating = false;
          this.toastService.sendToast({
            text: "WORK_RATING_CLEARED",
            classes: "bg-form-success",
          });
          this.cdr.markForCheck();
        },
        error: () => {
          this.isClearingRating = false;
          this.toastService.sendToast({
            text: "ERROR_UPDATE",
            classes: "bg-form-error",
          });
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(clearRatingSubscription);
  }

  private clearRatingFromCurrentUser(item: WorksModel): void {
    const currentUser = this.authService.user ?? this.user;
    if (!currentUser) {
      return;
    }

    let collectionKey:
      | "movies"
      | "series"
      | "games"
      | "mangas"
      | "comics"
      | "books";

    switch (item.type) {
      case WorksTypes.Movie:
        collectionKey = "movies";
        break;
      case WorksTypes.Series:
      case WorksTypes.Animation:
        collectionKey = "series";
        break;
      case WorksTypes.Game:
        collectionKey = "games";
        break;
      case WorksTypes.Manga:
        collectionKey = "mangas";
        break;
      case WorksTypes.Comic:
        collectionKey = "comics";
        break;
      case WorksTypes.Book:
        collectionKey = "books";
        break;
      default:
        return;
    }

    let hasClearedEntry = false;
    const entries = (currentUser[collectionKey] ?? []) as UserWork[];
    const updatedEntries = entries.map((entry) => {
      if (entry._id?.toString() !== item._id?.toString()) {
        return entry;
      }

      const updatedEntry = {...entry};
      delete updatedEntry.rating;
      hasClearedEntry = true;
      return updatedEntry;
    });

    if (!hasClearedEntry) {
      return;
    }

    const updatedUser = {...currentUser, [collectionKey]: updatedEntries};
    this.user = updatedUser;
    this.authService.user = updatedUser;
  }

  remove(item: WorksModel, event: Event, listId?: string) {
    const linkElement = event.target as HTMLLinkElement;

    if (linkElement.getAttribute("disabled") === null && this.user) {
      this.deleteConfirmLabel = "DELETING";

      const removeWorkSubscription = this.worksService
        .removeWorkById(this.user._id, item._id, item.type, listId)
        .subscribe({
          next: () => {
            this.closeModal("dlt_" + item._id);
            this.closeModal(item._id);
            this.deleteConfirmLabel = "YES_PLEASE";
            const toastKey = listId
              ? "WORK_REMOVED_FROM_COLLECTION"
              : "WORK_DELETED";
            let workDeletedString = "";
            const translationSubscription = this.translate
              .get(toastKey)
              .subscribe((translatedText: string) => {
                workDeletedString = translatedText;
              });
            this.subscriptions.push(translationSubscription);
            this.toastService.sendToast({
              text: workDeletedString,
              classes: "bg-form-success",
            });
            this.cdr.markForCheck();
          },
          error: () => {
            this.deleteConfirmLabel = "YES_PLEASE";
            this.cdr.markForCheck();
          },
        });
      this.subscriptions.push(removeWorkSubscription);
    }
  }

  private readonly handleOnlineEvent = () => {
    this.updateOnlineStatus(true);
  };

  private readonly handleOfflineEvent = () => {
    this.updateOnlineStatus(false);
  };

  private initializeIosConnectivity(): void {
    if (PlatformService.getPlatform() !== "ios") {
      return;
    }

    void this.refreshIosConnectivity();
    this.iosConnectivityListener = App.addListener(
      "appStateChange",
      ({isActive}) => {
        if (isActive) {
          void this.refreshIosConnectivity();
        }
      }
    );
  }

  private async refreshIosConnectivity(): Promise<void> {
    try {
      const response = await CapacitorHttp.get({
        url: `${environment.apiDomain}/version`,
        connectTimeout: 5000,
        readTimeout: 5000,
      });
      this.updateOnlineStatus(response.status > 0);
    } catch {
      this.updateOnlineStatus(false);
    }
  }

  private updateOnlineStatus(online: boolean): void {
    this.ngZone.run(() => {
      this.online = online;
      this.cdr.markForCheck();
    });
  }

  public onKeyUp(
    event: KeyboardEvent,
    type: string,
    backwardProgress?: boolean | VolumesBackwardProgress,
    workType?: WorksTypes,
    modalId?: string,
    item?: WorksModel,
    list?: string
  ): void {
    if (event.key === "Enter" || event.key === " ") {
      this.handleKeyUpAction(
        type,
        backwardProgress,
        workType,
        modalId,
        item,
        event,
        list
      );
    }
  }

  private handleKeyUpAction(
    type: string,
    backwardProgress: boolean | VolumesBackwardProgress | undefined,
    workType: WorksTypes | undefined,
    modalId: string | undefined,
    item: WorksModel | undefined,
    event: KeyboardEvent,
    list: string | undefined
  ): void {
    switch (type) {
      case "closeModal":
        if (modalId) {
          this.closeModal(modalId);
        }
        break;

      case "remove":
        if (item) {
          this.remove(item, event, list);
        }
        break;

      case "backwardProgress":
        if (backwardProgress && workType) {
          this.handleBackwardProgress(backwardProgress, workType);
        }
        break;

      case "backwardProgressAll":
        if (backwardProgress && workType) {
          this.handleBackwardProgressAll(backwardProgress, workType);
        }
        break;

      default:
        break;
    }
  }

  private handleBackwardProgress(
    backwardProgress: boolean | VolumesBackwardProgress,
    workType: WorksTypes
  ): void {
    switch (workType) {
      case WorksTypes.Book:
        this.bookService.backwardProgress = backwardProgress as boolean;
        break;
      case WorksTypes.Game:
        this.gameService.backwardProgress = backwardProgress as boolean;
        break;
      case WorksTypes.Manga:
        this.mangaService.backwardProgress =
          backwardProgress as VolumesBackwardProgress;
        break;
      case WorksTypes.Comic:
        this.comicService.backwardProgress =
          backwardProgress as VolumesBackwardProgress;
        break;
      case WorksTypes.Series:
        this.seriesService.backwardProgress = backwardProgress as boolean;
        break;
      default:
        break;
    }
  }

  private handleBackwardProgressAll(
    backwardProgress: boolean | VolumesBackwardProgress,
    workType: WorksTypes
  ): void {
    switch (workType) {
      case WorksTypes.Manga:
        this.mangaService.backwardProgressAll =
          backwardProgress as VolumesBackwardProgress;
        break;
      case WorksTypes.Comic:
        this.comicService.backwardProgressAll =
          backwardProgress as VolumesBackwardProgress;
        break;
      default:
        break;
    }
  }

  private reloadForAppVersion(nextVersion: string): void {
    if (this.isAppVersionReloading) {
      return;
    }

    const lastReloadedVersion = sessionStorage.getItem(
      this.appReloadedVersionSessionKey
    );
    if (lastReloadedVersion === nextVersion) {
      return;
    }

    sessionStorage.setItem(this.appUpdatedVersionSessionKey, nextVersion);
    sessionStorage.setItem(this.appReloadedVersionSessionKey, nextVersion);
    this.isAppVersionReloading = true;
    location.reload();
  }

  private handleAppVersionUpdate(response: AppVersionInfo): void {
    if (!response?.version || response.version === "0") {
      this.cdr.markForCheck();
      return;
    }

    if (!this.lastKnownAppVersion) {
      this.lastKnownAppVersion = response.version;
    } else if (this.lastKnownAppVersion !== response.version) {
      this.lastKnownAppVersion = response.version;
      if (!this.platformService.isNativePlatform()) {
        this.reloadForAppVersion(response.version);
        return;
      }
    }

    this.cdr.markForCheck();
  }

  private handleNavigationStart(event: NavigationStart): void {
    this.routeNavigationPending = this.shouldShowRouteTransitionSplash(
      event.url
    );
    this.updateRouteLayoutState(event.url);
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    this.updateRouteLayoutState(event.urlAfterRedirects);
    this.routeNavigationPending = false;
    this.openNewsAndFounderModalsIfAvailable();
  }

  private handleNavigationCancel(event: NavigationCancel): void {
    if (event.code === NavigationCancellationCode.Redirect) {
      return;
    }

    this.clearRouteLayoutTransition();
  }

  private clearRouteLayoutTransition(): void {
    this.routeNavigationPending = false;
    this.updateRouteLayoutState(this.router.url);
  }

  private shouldShowRouteTransitionSplash(nextUrl: string): boolean {
    return (
      this.hasCurrentDedicatedRouteShell() ||
      this.hasDedicatedRouteShell(nextUrl) ||
      this.shouldExpectOnboardingRedirect(nextUrl)
    );
  }

  private hasCurrentDedicatedRouteShell(): boolean {
    return (
      this.isAdminRoute ||
      this.isOnboardingRoute ||
      this.isCompassRecommendationsTunnelRoute ||
      this.isLegalCompletionRoute ||
      this.isPublicWorkRoute
    );
  }

  private hasDedicatedRouteShell(url: string): boolean {
    const segments = this.getRouteSegments(url);

    return (
      segments[0] === "admin" ||
      segments[0] === "onboarding" ||
      segments[0] === "complete-signup" ||
      segments[0] === "work" ||
      this.getCompassTunnelStep(segments) !== undefined
    );
  }

  private shouldExpectOnboardingRedirect(nextUrl: string): boolean {
    const user = this.user ?? this.authService.user;
    const segments = this.getRouteSegments(nextUrl);

    return (
      !!user &&
      !user.onboardedAt &&
      segments[0] !== "onboarding" &&
      segments[0] !== "complete-signup"
    );
  }

  private updateRouteLayoutState(url: string): void {
    const segments = this.getRouteSegments(url);
    const compassTunnelStep = this.getCompassTunnelStep(segments);

    this.isCompassRecommendationsTunnelRoute =
      segments[0] === "compass" && compassTunnelStep !== undefined;
    this.isOnboardingRoute = segments[0] === "onboarding";
    this.isAdminRoute = segments[0] === "admin";
    this.isLegalCompletionRoute = segments[0] === "complete-signup";
    this.isPublicWorkRoute = segments[0] === "work";
    this.isAccountActivationRoute = [
      "account-activation",
      "email-change",
    ].includes(segments[0]);
    this.isEmailChangeRoute = segments[0] === "email-change";
    const routeClass =
      segments[0] === "users"
        ? segments[2] ?? "user-profile"
        : segments[segments.length - 1] ?? "home";
    this.currentRouteClass = compassTunnelStep ?? routeClass;

    const usesPrimaryStatusBarSurface = this.usesPrimaryStatusBarSurface();
    document.body.classList.toggle(
      "native-status-bar-primary",
      this.platformService.isNativePlatform() && usesPrimaryStatusBarSurface
    );
    this.syncPageTheme();

    if (this.isLegalCompletionRoute) {
      this.closeNewsAndFounderModals();
    }
  }

  private usesPrimaryStatusBarSurface(): boolean {
    return this.isCompassRecommendationsTunnelRoute || this.isOnboardingRoute;
  }

  private syncPageTheme(): void {
    const theme = this.isEmailChangeRoute
      ? "light"
      : this.authService.user?.theme ?? "light";
    document.documentElement.className = theme;
    this.platformService.syncNativeStatusBar(
      theme,
      this.usesPrimaryStatusBarSurface()
    );
  }

  private openNewsAndFounderModalsIfAvailable(): void {
    if (
      this.shouldSuppressNewsAndFounderModals() ||
      !this.logged ||
      !this.user
    ) {
      return;
    }

    this.openFounderPremiumModalIfNeeded(this.user);
  }

  private closeNewsAndFounderModals(): void {
    this.closeModalIfOpen("news");
    this.closeModalIfOpen(this.founderPremiumModalId);
  }

  private closeModalIfOpen(modalId: string): void {
    if (this.modalService.isModalOpen(modalId)) {
      this.modalService.closeModal(modalId);
    }
  }

  private shouldSuppressNewsAndFounderModals(): boolean {
    const currentRouteSegment = this.getRouteSegments(
      this.getCurrentBrowserUrl()
    )[0];

    return (
      this.isLegalCompletionRoute || currentRouteSegment === "complete-signup"
    );
  }

  private getRouteSegments(url: string): string[] {
    return url.split(/[?#]/)[0].split("/").filter(Boolean);
  }

  private getCompassTunnelStep(segments: string[]): string | undefined {
    return segments[0] === "compass"
      ? segments.find((segment) => this.compassTunnelSteps.has(segment))
      : undefined;
  }

  private getCurrentBrowserUrl(): string {
    return `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
  }

  ngOnDestroy() {
    globalThis.removeEventListener("online", this.handleOnlineEvent);
    globalThis.removeEventListener("offline", this.handleOfflineEvent);
    void this.iosConnectivityListener?.then((listener) => listener.remove());
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.socketCleanups.forEach((cleanup) => {
      cleanup();
    });
  }
}
